import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../common/Layout';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import TypingIndicator from './TypingIndicator';
import QuickActions from './QuickActions';
import { apiService } from '../../services/api';
import '../../styles/features/aicoach.css';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: {
    tools_used?: string[];
    analysis_timestamp?: string;
    api_calls_made?: number;
    functionCalls?: string[];
    dataReturned?: any;
    isLoading?: boolean;
  };
}

const ChatInterface: React.FC = () => {
  const location = useLocation();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: `Ask about your positions, a roll, the market, or a setup you're sizing up. I answer from your book and today's data.`,
      timestamp: new Date(),
    }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState('');
  // Active conversation_id when this page is opened from a handoff (e.g.
  // continuing a Strategy Review). null = use the user's default conversation.
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasAutoSentRef = useRef(false);
  const hasHydratedRef = useRef(false);

  // Hydrate from a specific conversation (handoff from another surface).
  useEffect(() => {
    if (hasHydratedRef.current) return;

    const stateConvId = (location.state as any)?.conversationId;
    const searchParams = new URLSearchParams(location.search);
    const urlConvId = searchParams.get('conversation_id');
    const conversationId = stateConvId ?? (urlConvId ? Number(urlConvId) : null);

    if (!conversationId || Number.isNaN(conversationId)) return;

    hasHydratedRef.current = true;
    // Block the initial-prompt auto-send path — handoffs already have a thread.
    hasAutoSentRef.current = true;

    (async () => {
      try {
        const conv = await apiService.getAICoachConversation(conversationId);
        // Skip the strategy-review seed user message (raw performance prompt
        // is huge and not meant to be shown — it lives only in the LLM context).
        const visible = conv.messages.filter(
          (m) => !(m.role === 'user' && m.metadata?.kind === 'strategy_review_seed')
        );
        const hydrated: Message[] = visible.map((m) => ({
          id: `db-${m.id}`,
          role: m.role,
          content: m.content,
          timestamp: m.created_at ? new Date(m.created_at) : new Date(),
        }));
        if (hydrated.length === 0) return;
        setMessages(hydrated);
        setActiveConversationId(conversationId);
      } catch (e) {
        console.error('Failed to hydrate AI Coach conversation:', e);
      }
    })();
  }, [location.state, location.search]);

  // Handle URL query parameters and navigation state for auto-input
  useEffect(() => {
    // Prevent sending the same prompt twice
    if (hasAutoSentRef.current) {
      return;
    }

    // Check for prompt from navigation state (from AICoachWidget)
    const statePrompt = (location.state as any)?.initialPrompt;

    // Check for prompt from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const urlPrompt = searchParams.get('prompt');

    const promptToUse = statePrompt || urlPrompt;

    if (promptToUse) {
      hasAutoSentRef.current = true;
      setInputValue(promptToUse);
      // Auto-send the message after a short delay
      setTimeout(() => {
        handleSendMessage(promptToUse);
      }, 500);
    }
  }, [location.search, location.state]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-focus input when AI finishes responding
  useEffect(() => {
    if (!isTyping && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isTyping]);

  const handleSendMessage = async (message: string) => {
    if (!message.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: message,
      timestamp: new Date(),
    };

    const assistantId = (Date.now() + 1).toString();
    const assistantStub: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage, assistantStub]);
    setInputValue('');
    setIsTyping(true);

    let receivedAny = false;
    let errored = false;

    const appendChunk = (chunk: string) => {
      if (!chunk) return;
      receivedAny = true;
      if (isTyping) {
        // First chunk arrived — hide the "thinking…" indicator
        setIsTyping(false);
      }
      setMessages(prev =>
        prev.map(m => (m.id === assistantId ? { ...m, content: m.content + chunk } : m))
      );
    };

    try {
      await apiService.streamAICoachChat(
        message,
        appendChunk,
        () => {
          setIsTyping(false);
          if (!receivedAny && !errored) {
            setMessages(prev =>
              prev.map(m =>
                m.id === assistantId ? { ...m, content: 'Sorry — I had nothing to say there. Try rephrasing?' } : m
              )
            );
          }
        },
        (errMsg: string) => {
          errored = true;
          console.error('AI Coach stream error:', errMsg);
          setIsTyping(false);
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantId
                ? { ...m, content: 'Sorry — I hit an error processing that. Please try again.' }
                : m
            )
          );
        },
        activeConversationId ?? undefined,
        // A blank page (first load or after Clear) starts a fresh thread, so the
        // coach sees exactly the conversation on screen, not an older session.
        activeConversationId == null,
        (id) => setActiveConversationId(id),
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      setIsTyping(false);
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantId
            ? { ...m, content: 'Sorry — I hit an error processing that. Please try again.' }
            : m
        )
      );
    }
  };

  const handleQuickAction = (action: string) => {
    handleSendMessage(action);
  };

  const handleClearChat = () => {
    setMessages([
      {
        id: '1',
        role: 'assistant',
        content: `Cleared. What's on your mind — positions, markets, a setup you're sizing up?`,
        timestamp: new Date(),
      }
    ]);
    // Detach from the current thread; the next message starts a fresh one.
    setActiveConversationId(null);
  };

  return (
    <Layout>
      <div className="app-chat-container">
        {/* Header */}
        <div className="app-chat-header">
          <div className="app-chat-title-section">
            <h1>AI Coach</h1>
            <p className="app-chat-subtitle">Knows your positions, the market and the news. Not advice: it explains, you decide.</p>
          </div>
          <button
            onClick={handleClearChat}
            className="app-chat-clear-button"
          >
            Clear
          </button>
        </div>

        {/* Messages Container */}
        <div className="app-chat-messages">
          {messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}
          {isTyping && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        <QuickActions onSelectAction={handleQuickAction} />

        {/* Input Area */}
        <MessageInput
          ref={inputRef}
          value={inputValue}
          onChange={setInputValue}
          onSend={handleSendMessage}
          disabled={isTyping}
        />
      </div>
    </Layout>
  );
};

export default ChatInterface;